import { useQuery } from '@tanstack/react-query';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';

import type { DirectoryConfig, DirectoryConfigResponse } from './types';

const IS_DEV = import.meta.env.DEV;
const MOCK_DELAY_MS = 800;

const DEFAULT_CARD_FIELDS: DirectoryConfig['cardFields'] = {
  company: true,
  city: true,
  phone: true,
  cuisine: true,
  sub_catname: true,
  brand: true,
  segment: true,
};

const mockDelay = () => {
  return new Promise<void>((resolve) => {
    return setTimeout(resolve, MOCK_DELAY_MS);
  });
};

const loadMockConfig = async (appId: string | null) => {
  await mockDelay();
  try {
    const module = (await import(`./devOverrides/directory-config-${appId}.json`)) as {
      default: DirectoryConfigResponse;
    };
    return module.default;
  } catch {
    const fallback = (await import('./devOverrides/directory-config-default.json')) as {
      default: DirectoryConfigResponse;
    };
    return fallback.default;
  }
};

type DirectoryConfigState =
  | { status: 'loading' }
  | { status: 'found'; config: DirectoryConfig }
  | { status: 'not_found' };

function getQueryKey(appId: string | null, enableMock: boolean) {
  return ['directoryConfig', appId, enableMock] as const;
}

function getQueryFn(appId: string | null, enableMock: boolean) {
  return async () => {
    const shouldUseMock = enableMock && IS_DEV;
    return (
      shouldUseMock ? await loadMockConfig(appId) : await APIHandler.getDirectoryConfig(appId)
    ) as DirectoryConfigResponse;
  };
}

export const useDirectoryConfig = (enableMock = false): DirectoryConfigState => {
  const { appId } = useSettings();
  const { data, isLoading } = useQuery({
    queryKey: getQueryKey(appId, enableMock),
    queryFn: getQueryFn(appId, enableMock),
    enabled: appId !== null && appId !== undefined,
  });

  if (isLoading || !data) return { status: 'loading' };
  if (data.status !== 'success') return { status: 'not_found' };
  return {
    status: 'found',
    config: { map: data.map, listings: data.listings, cardFields: data.cardFields ?? DEFAULT_CARD_FIELDS },
  };
};
